import { AxiosRequestConfig, Method } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, heliosAPI } from 'utils/services/heliosAPI';

type ApiConfig = Omit<AxiosRequestConfig, 'method'>;

type useAPIResponse<T> = [
    { data?: T; error?: ApiError; loading: boolean },
    (apiConfig?: ApiConfig) => Promise<T>,
];

export const useAPI = <T,>(
    url: string = '',
    method: Method,
    manual = false
): useAPIResponse<T> => {
    const [data, setData] = useState<T>();
    const [error, setError] = useState<ApiError>();
    const [loading, setLoading] = useState(!manual);

    const fetchData = useCallback(
        async (apiConfig?: ApiConfig) => {
            if (!url && !apiConfig?.url && !apiConfig?.baseURL) {
                throw new Error('Missing URL for API request.');
            }

            setError(undefined);
            setLoading(true);
            try {
                const response = await heliosAPI.request<T>({
                    url,
                    method,
                    ...apiConfig,
                });

                setData(response.data);
                setLoading(false);

                return response.data;
            } catch (e) {
                const error = e as ApiError;

                /*
                 * TL;DR; For explicitly aborted request do not set state
                 *   because there is subsequent request already in progress.
                 *
                 * At first it seems that `error` and `loading` should be always
                 * set when request finishes, and received error code should be
                 * irrelevant, i.e. it should not matter whether request was
                 * errored or explicitly aborted. However, this logic here
                 * slightly simplifies things by assuming the following
                 * heuristics: as of now request is programmed to be explicitly
                 * aborted only when subsequent request is made. In such case it
                 * does not make much sense to set any state because another
                 * request is already in progress. We are letting it take state
                 * management over from here. It is also debatable whether this
                 * function should resolve with an empty value or `throw` in
                 * said scenario but adding `return` or moving `throw` behind
                 * condition messes up types so keep it as-is.
                 */
                if (error?.code !== 'ERR_CANCELED') {
                    setError(error);
                    setLoading(false);
                }

                throw error;
            }
        },
        [url, method]
    );

    useEffect(() => {
        if (!manual && url) {
            fetchData();
        }
    }, [fetchData, manual, url]);

    return [{ data, error, loading }, fetchData];
};

export const useGet = <T,>(url: string, manual = false) =>
    useAPI<T>(url, 'GET', manual);

// Opinionated usage of POST/PATCH requests to only ever be triggered manually
export const usePost = <T,>(url?: string) => useAPI<T>(url, 'POST', true);
export const usePatch = <T,>(url?: string) => useAPI<T>(url, 'PATCH', true);
export const usePut = <T,>(url?: string) => useAPI<T>(url, 'PUT', true);
export const useDelete = <T,>(url?: string) => useAPI<T>(url, 'DELETE', true);
